import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Td } from "../../features/web3/forex/components/table";
import { Pagination } from "../../features/web3/hyperliquid/components/Pagination";
import { PAGE_SIZES } from "./api";

export const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none";
export const labelCls =
  "block text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

const DASH = <span className="text-muted-foreground">—</span>;

// null means unset (no stop, no expiry) and must never render as 0 or a date.
export const orDash = (v) => (v == null || v === "" ? DASH : v);

// Money and lots arrive as exact strings; shown as-is, never re-rounded.
export function Money({ value }) {
  if (value == null) return DASH;
  const tone = value.startsWith("-")
    ? "text-red-400"
    : Number(value) > 0
      ? "text-emerald-400"
      : "text-muted-foreground";
  return <span className={`font-mono ${tone}`}>{value}</span>;
}

const Tag = ({ tone, children }) => (
  <span
    className={`inline-flex rounded-full border px-1.5 py-px text-[10px] font-medium ${tone}`}
  >
    {children}
  </span>
);

export const PartialTag = () => (
  <Tag tone="ml-1.5 border-amber-500/20 bg-amber-500/10 text-amber-400">Partial</Tag>
);

// Every owner field the API sends, one column each, so nothing blurs into one
// line. Pair with ACCOUNT_COLUMNS.
export const ACCOUNT_COLUMNS = [
  { label: "Holder" },
  { label: "Login" },
  { label: "Username" },
  { label: "Tier" },
  { label: "Purpose" },
  { label: "Unattributed" },
];

const AMBER = "border-amber-500/20 bg-amber-500/10 text-amber-400";

// Login and username link to that account's or user's whole history.
export const historyUrl = (field, value) =>
  `/forex-admin/risk/account?${new URLSearchParams({ [field]: value })}`;

export function AccountCells({ row }) {
  const pick = (field, value) =>
    value ? (
      <Link to={historyUrl(field, value)} className="hover:text-primary hover:underline">
        {value}
      </Link>
    ) : (
      orDash(value)
    );
  return (
    <>
      <Td className="whitespace-nowrap font-medium">{orDash(row.holderName)}</Td>
      <Td className="font-mono">{pick("login", row.login)}</Td>
      <Td className="font-mono">{pick("username", row.username)}</Td>
      <Td className="text-xs">{orDash(row.tier)}</Td>
      {/* Copy-trading accounts move in correlated blocks: worth spotting. */}
      <Td className="whitespace-nowrap text-xs">
        {row.purpose === "COPY_TRADING" ? (
          <Tag tone="border-blue-500/20 bg-blue-500/10 text-blue-400">COPY_TRADING</Tag>
        ) : (
          orDash(row.purpose)
        )}
      </Td>
      {/* Not linked to a Ginox user: a username filter never matches these. */}
      <Td className="text-xs">
        {row.unattributed ? <Tag tone={AMBER}>Yes</Tag> : "No"}
      </Td>
    </>
  );
}

// A labelled text/date/select input bound to a draft-filters object.
export function FilterField({ label, name, draft, setDraft, type = "text", options, placeholder }) {
  const onChange = (e) => setDraft((d) => ({ ...d, [name]: e.target.value }));
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {options ? (
        <select name={name} value={draft[name] ?? ""} onChange={onChange} className={inputCls}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={draft[name] ?? ""}
          onChange={onChange}
          placeholder={placeholder}
          className={inputCls}
        />
      )}
    </div>
  );
}

export const PAGE_SIZE_OPTIONS = PAGE_SIZES.map((n) => ({ value: String(n), label: `${n} / page` }));

// Filters are edited as a draft and only sent on Apply, so typing a login does
// not fire a request per keystroke against a 60/min limit.
export function FilterBar({ children, onApply, onReset }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onApply();
      }}
      className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4"
    >
      {children}
      <div className="flex gap-2">
        <Button type="submit" size="sm">
          Apply
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onReset}>
          Reset
        </Button>
      </div>
    </form>
  );
}

// Page-number envelope ({ currentPage, totalPages, totalRows, pageSize }) onto
// the shared Pagination's shape.
export function PagePager({ data, onChange, label }) {
  if (!data) return null;
  return (
    <Pagination
      data={{
        page: data.currentPage,
        totalPages: data.totalPages,
        total: data.totalRows,
        limit: data.pageSize,
      }}
      onChange={onChange}
      label={label}
    />
  );
}
